import { useMemo } from "react";
import type { FilterState, ScopeFilters, DrillLevel } from "@/types/filters";
import type { SchedulingRow } from "@/types/scheduling";
import { applyBaseFilter, filterRowsByDateRange, filterRowsByScope } from "@/lib/metrics";
import { EMPTY_RANGE, getPriorYearRange } from "@/lib/periods";

// Aggregate revenue totals underlying every KPI/derived metric — computed
// once per row set (current scope, prior year same scope, or NVA/unscoped)
// so current/priorYear/nva always come from the exact same formula.
interface RevenueTotals {
  totalMedRev: number;
  totalScheduledMin: number;
  totalApptMin: number;
  totalCompleted: number;
  distinctDvms: number;
  // Doctor-DAYS, not calendar days: one doctor scheduled on one date. The
  // denominator for every "per DVM per day" figure, and the same definition
  // metrics.ts uses so the Revenue and Utilization tabs cannot disagree about
  // what a DVM-day is.
  dvmDays: number;
}

function sumTotals(rows: SchedulingRow[]): RevenueTotals {
  let totalMedRev = 0;
  let totalScheduledMin = 0;
  let totalApptMin = 0;
  let totalCompleted = 0;
  const dvms = new Set<string>();
  const dvmDays = new Set<string>();
  for (const r of rows) {
    totalMedRev += Number(r.medical_net_sales_amount) || 0;
    totalScheduledMin += Number(r.scheduled_minutes) || 0;
    totalApptMin += Number(r.completed_appointment_minutes) || 0;
    totalCompleted += Number(r.completed_appointments) || 0;
    dvms.add(r.resource_id);
    dvmDays.add(`${r.resource_id}|${r.schedule_date}`);
  }
  return {
    totalMedRev,
    totalScheduledMin,
    totalApptMin,
    totalCompleted,
    distinctDvms: dvms.size,
    dvmDays: dvmDays.size,
  };
}

export interface RevenueMetricSet {
  current: number;
  priorYear: number;
  nva: number;
}

export interface RevenueKPIs {
  medRevPerDvm: RevenueMetricSet;
  medRevPerSeenHour: RevenueMetricSet;
  medRevPerSchedHour: RevenueMetricSet;
  medRevPerAppt: RevenueMetricSet;
  apptsPerDvmDay: RevenueMetricSet;
}

function deriveMetrics(t: RevenueTotals) {
  return {
    perDvm: t.distinctDvms > 0 ? t.totalMedRev / t.distinctDvms : 0,
    perSeenHour: t.totalApptMin > 0 ? t.totalMedRev / (t.totalApptMin / 60) : 0,
    perSchedHour: t.totalScheduledMin > 0 ? t.totalMedRev / (t.totalScheduledMin / 60) : 0,
    perAppt: t.totalCompleted > 0 ? t.totalMedRev / t.totalCompleted : 0,
    apptsPerDvmDay: t.dvmDays > 0 ? t.totalCompleted / t.dvmDays : 0,
  };
}

export interface RevenueDrillRow {
  id: string;
  label: string;
  subLabel: string | null;
  scheduledDays: number;
  dvmDays: number;
  scheduledMinutes: number;
  apptMinutes: number;
  completedAppts: number;
  medicalRevenue: number;
  // null when the entity has no rows in the prior-year window
  pyMedicalRevenue: number | null;
}

export interface RevenuePinnedRow {
  label: string;
  dvmDays: number;
  scheduledMinutes: number;
  apptMinutes: number;
  completedAppts: number;
  medicalRevenue: number;
  pyMedicalRevenue: number | null;
}

function groupKey(row: SchedulingRow, level: DrillLevel): { key: string; label: string } {
  switch (level) {
    case "country":
      return { key: row.country_code, label: row.country_code };
    case "division":
      return { key: row.division_name, label: row.division_name };
    case "group":
      return { key: row.group_name, label: row.group_name };
    case "site":
      return { key: row.location_id, label: `${row.location_id} - ${row.location_name}` };
    case "dvm":
      return { key: row.resource_id, label: row.resource_full_name };
  }
}

function groupRows(
  rows: SchedulingRow[],
  level: DrillLevel
): Map<string, { label: string; rows: SchedulingRow[] }> {
  const map = new Map<string, { label: string; rows: SchedulingRow[] }>();
  for (const row of rows) {
    const { key, label } = groupKey(row, level);
    if (!map.has(key)) map.set(key, { label, rows: [] });
    map.get(key)!.rows.push(row);
  }
  return map;
}

// DVM rows get their primary site (most revenue) as a sublabel, formatted
// "<id> - <name>" to match how sites are labeled at the Site drill level.
function primarySite(gRows: SchedulingRow[]): string | null {
  const revBySite = new Map<string, { label: string; rev: number }>();
  for (const r of gRows) {
    const e = revBySite.get(r.location_id) ?? {
      label: `${r.location_id} - ${r.location_name}`,
      rev: 0,
    };
    e.rev += Number(r.medical_net_sales_amount) || 0;
    revBySite.set(r.location_id, e);
  }
  // Ties broken by label rather than by iteration order -- see the same fix in
  // useCancellationData. Equal revenue at two sites is rare but real, and the
  // winner must not depend on row order.
  let best: string | null = null;
  let bestRev = -Infinity;
  for (const { label, rev } of revBySite.values()) {
    if (rev > bestRev || (rev === bestRev && best !== null && label < best)) {
      bestRev = rev;
      best = label;
    }
  }
  return best;
}

const DRILL_LEVELS: DrillLevel[] = ["country", "group", "division", "site", "dvm"];

// Scope narrowed to only the filters up to and including the given ancestor
// level — mirrors useDrillData's buildPartialScope, so pinned ancestor
// averages behave identically between the Utilization and Revenue tabs.
function buildPartialScope(scope: ScopeFilters, upToLevel: DrillLevel): ScopeFilters {
  const idx = DRILL_LEVELS.indexOf(upToLevel);
  return {
    countryCode: idx >= 0 ? scope.countryCode : null,
    groupName: idx >= 1 ? scope.groupName : null,
    divisionName: idx >= 2 ? scope.divisionName : null,
    locationId: idx >= 3 ? scope.locationId : null,
    resourceId: null,
  };
}

function ancestorLabel(level: DrillLevel, scope: ScopeFilters): string {
  switch (level) {
    case "country":
      return scope.countryCode ? `${scope.countryCode} Avg.` : "NVA Avg. (Eligible Sites)";
    case "group":
      return scope.groupName ? `${scope.groupName} Avg.` : "Group Avg.";
    case "division":
      return scope.divisionName ? `${scope.divisionName} Avg.` : "Division Avg.";
    case "site":
      return "Site Avg.";
    case "dvm":
      return "DVM Avg.";
  }
}

export function useRevenueData(
  rows: SchedulingRow[],
  filters: FilterState,
  startDate: string,
  endDate: string,
  drillLevel: DrillLevel
): { kpis: RevenueKPIs; drillRows: RevenueDrillRow[]; pinned: RevenuePinnedRow[] } {
  return useMemo(() => {
    const base = applyBaseFilter(rows, filters.excludeZeroApptDays);
    const dated = filterRowsByDateRange(base, startDate, endDate);
    const scoped = filterRowsByScope(dated, filters.scope);

    const pyRange = getPriorYearRange({ startDate, endDate }, filters.period) ?? EMPTY_RANGE;
    const pyDated = filterRowsByDateRange(base, pyRange.startDate, pyRange.endDate);
    const pyScoped = filterRowsByScope(pyDated, filters.scope);

    // current = user's scope, this period. priorYear = user's scope, PY window.
    // nva = every eligible site, this period (ignores scope) — the same
    // "NVA Avg." convention used throughout the Utilization tab.
    const currentTotals = sumTotals(scoped);
    const pyTotals = sumTotals(pyScoped);
    const nvaTotals = sumTotals(dated);

    const cur = deriveMetrics(currentTotals);
    const py = deriveMetrics(pyTotals);
    const nva = deriveMetrics(nvaTotals);

    const kpis: RevenueKPIs = {
      medRevPerDvm: { current: cur.perDvm, priorYear: py.perDvm, nva: nva.perDvm },
      medRevPerSeenHour: {
        current: cur.perSeenHour,
        priorYear: py.perSeenHour,
        nva: nva.perSeenHour,
      },
      medRevPerSchedHour: {
        current: cur.perSchedHour,
        priorYear: py.perSchedHour,
        nva: nva.perSchedHour,
      },
      medRevPerAppt: { current: cur.perAppt, priorYear: py.perAppt, nva: nva.perAppt },
      apptsPerDvmDay: {
        current: cur.apptsPerDvmDay,
        priorYear: py.apptsPerDvmDay,
        nva: nva.apptsPerDvmDay,
      },
    };

    // Prior-year revenue per entity for the drill row's vs. PY column.
    const pyRevByKey = new Map<string, number>();
    for (const r of pyScoped) {
      const { key } = groupKey(r, drillLevel);
      pyRevByKey.set(key, (pyRevByKey.get(key) ?? 0) + (Number(r.medical_net_sales_amount) || 0));
    }

    const grouped = groupRows(scoped, drillLevel);
    const drillRows = Array.from(grouped.entries())
      .map(([id, { label, rows: gRows }]) => ({
        id,
        label,
        subLabel: drillLevel === "dvm" ? primarySite(gRows) : null,
        scheduledDays: new Set(gRows.map((r) => r.schedule_date)).size,
        dvmDays: new Set(gRows.map((r) => `${r.resource_id}|${r.schedule_date}`)).size,
        scheduledMinutes: gRows.reduce((s, r) => s + (Number(r.scheduled_minutes) || 0), 0),
        apptMinutes: gRows.reduce((s, r) => s + (Number(r.completed_appointment_minutes) || 0), 0),
        completedAppts: gRows.reduce((s, r) => s + Number(r.completed_appointments), 0),
        medicalRevenue: gRows.reduce((s, r) => s + Number(r.medical_net_sales_amount), 0),
        pyMedicalRevenue: pyRevByKey.get(id) ?? null,
      }))
      .sort((a, b) => b.medicalRevenue - a.medicalRevenue);

    // Pinned rows: NVA Avg. always; deeper ancestor averages only once that
    // level is actually selected in scope — mirrors useDrillData exactly so
    // the two tabs' drill-down tables behave identically.
    const currentIdx = DRILL_LEVELS.indexOf(drillLevel);
    const pinned: RevenuePinnedRow[] = DRILL_LEVELS.slice(0, currentIdx)
      .filter(
        (level) =>
          level === "country" ||
          (level === "group" && filters.scope.groupName) ||
          (level === "division" && filters.scope.divisionName) ||
          (level === "site" && filters.scope.locationId)
      )
      .map((level) => {
        const partialScope = buildPartialScope(filters.scope, level);
        const ancestorRows = filterRowsByScope(dated, partialScope);
        const pyAncestorRows = filterRowsByScope(pyDated, partialScope);
        return {
          label: ancestorLabel(level, filters.scope),
          dvmDays: new Set(ancestorRows.map((r) => `${r.resource_id}|${r.schedule_date}`)).size,
          scheduledMinutes: ancestorRows.reduce(
            (s, r) => s + (Number(r.scheduled_minutes) || 0),
            0
          ),
          apptMinutes: ancestorRows.reduce(
            (s, r) => s + (Number(r.completed_appointment_minutes) || 0),
            0
          ),
          completedAppts: ancestorRows.reduce((s, r) => s + Number(r.completed_appointments), 0),
          medicalRevenue: ancestorRows.reduce((s, r) => s + Number(r.medical_net_sales_amount), 0),
          pyMedicalRevenue:
            pyAncestorRows.length > 0
              ? pyAncestorRows.reduce((s, r) => s + Number(r.medical_net_sales_amount), 0)
              : null,
        };
      });

    return { kpis, drillRows, pinned };
  }, [rows, filters, startDate, endDate, drillLevel]);
}
